import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { TeamsAPI } from "@/lib/api";
import { orderedSections } from "@/lib/sections";
import { isVideoFileUrl, toPlayableVideoUrl, videoPosterUrl } from "@/lib/video";
import { transparentLogoUrl } from "@/lib/logo";
import SiteHeader from "@/components/SiteHeader";
import QRCodeCard from "@/components/PlayerQRCode";
import ShareButton from "@/components/ShareButton";
import ScanReveal from "@/components/ScanReveal";
import GoldenParticles from "@/components/GoldenParticles";
import { FullPageLoader } from "@/components/Loaders";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { getSport } from "@/lib/sports";
import { ArrowLeft, Pencil, Trophy, Video, MapPin, Calendar, Users } from "lucide-react";

const HighlightEmbed = ({ h }) => {
  const url = h.url || "";
  let embedUrl = url;
  if (h.type === "youtube" || /youtube\.com|youtu\.be/.test(url)) {
    const m = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/,
    );
    if (m) embedUrl = `https://www.youtube.com/embed/${m[1]}`;
  } else if (h.type === "vimeo" || /vimeo\.com/.test(url)) {
    const m = url.match(/vimeo\.com\/(\d+)/);
    if (m) embedUrl = `https://player.vimeo.com/video/${m[1]}`;
  }
  const isVideoFile = isVideoFileUrl(url, h.type);
  return (
    <div className="relative aspect-video w-full rounded-xl overflow-hidden border border-white/10 bg-black">
      {isVideoFile ? (
        <video src={toPlayableVideoUrl(url)} controls playsInline preload="metadata" poster={h.poster || videoPosterUrl(url) || undefined} className="w-full h-full object-cover" />
      ) : (
        <iframe
          src={embedUrl}
          title={h.title || "highlight"}
          className="w-full h-full"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  );
};

export default function TeamProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    TeamsAPI.get(slug)
      .then((p) => {
        if (alive) setProfile(p);
      })
      .catch(() => {
        if (alive) setError("Équipe introuvable");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading) {
    return <FullPageLoader caption="Chargement de l'équipe…" />;
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="font-display text-3xl font-semibold uppercase tracking-tight mb-3">
            Équipe introuvable
          </h1>
          <p className="text-zinc-400 font-body">
            {error || "Cette équipe n'existe pas."}
          </p>
        </div>
      </div>
    );
  }

  const { team, roster, tournaments_history: history } = profile;
  const sportDef = getSport(team.sport || "football");
  const accent = team.primary_color || "#0066FF";
  const isAdmin = user?.role === "admin";

  const profileUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/team/${team.slug}`
      : "";

  const secOrder = orderedSections("team", team.section_order);
  const secStyle = (k) => ({ order: 10 + secOrder.indexOf(k) });

  return (
    <ScanReveal slug={team.slug} kind="team">
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          background: team.cover_image_url
            ? `linear-gradient(180deg, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.85) 70%, #050505 100%), url(${team.cover_image_url}) center/cover`
            : `linear-gradient(180deg, ${accent}1A 0%, #050505 100%)`,
        }}
        data-testid="team-hero"
      >
        <GoldenParticles count={12} density="subtle" />
        <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-14 pb-12 md:pt-20 md:pb-16">
          {isAdmin && (
            <Link
              to="/teams"
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-[0.2em] mb-6"
            >
              <ArrowLeft className="w-3 h-3" /> Retour aux équipes
            </Link>
          )}

          <div className="flex flex-col md:flex-row md:items-end gap-6 md:gap-8">
            <div
              className="w-28 h-28 md:w-36 md:h-36 rounded-2xl overflow-hidden border-2 shrink-0 flex items-center justify-center bg-white/5 backdrop-blur"
              style={{ borderColor: accent }}
            >
              {team.logo_url ? (
                <img
                  src={transparentLogoUrl(team.logo_url)}
                  alt={team.name}
                  className="w-full h-full object-contain p-3"
                />
              ) : (
                <span
                  className="font-display font-black text-5xl"
                  style={{ color: accent }}
                >
                  {team.name?.[0]}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div
                className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] mb-2"
                style={{ color: accent }}
              >
                {[team.club_name, team.category].filter(Boolean).join(" · ") ||
                  "Équipe officielle"}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight leading-none">
                {team.name}
              </h1>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-zinc-400 text-xs sm:text-sm font-body">
                <span className="inline-flex items-center gap-1.5">
                  <Trophy className="w-3.5 h-3.5" /> {sportDef.label}
                </span>
                {team.season && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" /> Saison {team.season}
                  </span>
                )}
                {team.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> {team.location}
                  </span>
                )}
                {team.founded_year && (
                  <span className="inline-flex items-center gap-1.5">
                    Depuis {team.founded_year}
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <ShareButton
                kind="team"
                slug={team.slug}
                title={team.name}
                accent={accent}
              />
              {isAdmin && (
                <Link
                  to={`/admin/teams/${team.id}`}
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-300 border border-white/15 rounded-full px-4 py-2 hover:bg-white/10"
                  data-testid="team-edit-btn"
                >
                  <Pencil className="w-3 h-3" /> Modifier
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 flex flex-col gap-14">
        {/* BIO */}
        {team.bio && (
          <section data-testid="team-bio" style={secStyle("bio")}>
            <p className="text-zinc-300 font-body leading-relaxed text-base sm:text-lg max-w-3xl">
              {team.bio}
            </p>
          </section>
        )}

        {/* ROSTER */}
        <section data-testid="team-roster" style={secStyle("roster")}>
          <div className="flex items-center gap-3 mb-5">
            <Users className="w-4 h-4 text-zinc-400" />
            <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight">
              Effectif
            </h2>
            <span className="text-[10px] tabular-nums px-2 py-0.5 rounded bg-white/10 text-zinc-300 font-bold">
              {roster.length}
            </span>
          </div>
          {roster.length === 0 ? (
            <div className="metal-card rounded-xl p-8 text-center text-zinc-500 font-body text-sm">
              Aucun joueur lié à cette équipe pour le moment.
            </div>
          ) : (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
              data-testid="team-roster-grid"
            >
              {roster.map((p) => (
                <Link
                  key={p.id}
                  to={`/player/${p.slug}`}
                  data-testid={`roster-player-${p.slug}`}
                  className="metal-card rounded-xl overflow-hidden hover:border-white/30 transition-all group"
                >
                  <div
                    className="relative aspect-[3/4] overflow-hidden"
                    style={{
                      background: `linear-gradient(180deg, ${
                        p.primary_color || accent
                      }22, #0c0c10)`,
                    }}
                  >
                    {p.hero_image_url ? (
                      <img
                        src={p.hero_image_url}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span
                          className="font-display font-black text-6xl"
                          style={{ color: p.primary_color || accent }}
                        >
                          {p.name?.[0]}
                        </span>
                      </div>
                    )}
                    {p.jersey_number != null && (
                      <span
                        className="absolute top-2 right-2 font-display font-black text-2xl tabular-nums leading-none px-2 py-1 rounded bg-black/60 backdrop-blur"
                        style={{ color: p.primary_color || accent }}
                      >
                        #{p.jersey_number}
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="font-display font-bold uppercase tracking-tight truncate text-sm sm:text-base">
                      {p.name}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 truncate mt-0.5">
                      {[p.position, p.category].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* TOURNAMENTS HISTORY */}
        {history.length > 0 && (
          <section data-testid="team-tournaments-history" style={secStyle("history")}>
            <div className="flex items-center gap-3 mb-5">
              <Trophy className="w-4 h-4 text-zinc-400" />
              <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight">
                Historique des tournois
              </h2>
              <span className="text-[10px] tabular-nums px-2 py-0.5 rounded bg-white/10 text-zinc-300 font-bold">
                {history.length}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {history.map((t) => {
                const podium = t.rank && t.rank <= 3;
                return (
                  <Link
                    key={t.id}
                    to={`/tournament/${t.slug}`}
                    data-testid={`history-tournament-${t.slug}`}
                    className="metal-card rounded-xl overflow-hidden hover:border-white/30 transition-all"
                  >
                    <div
                      className="aspect-[16/9] relative overflow-hidden"
                      style={{
                        background: t.cover_image_url
                          ? `url(${t.cover_image_url}) center/cover`
                          : `linear-gradient(135deg, ${
                              t.primary_color || "#D4AF37"
                            }33, #0c0c10)`,
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      {podium && (
                        <span
                          className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-full text-black"
                          style={{
                            background:
                              "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
                          }}
                        >
                          <Trophy className="w-3 h-3" /> {t.rank}
                          {t.rank === 1 ? "er" : "ème"}
                        </span>
                      )}
                    </div>
                    <div className="p-3 sm:p-4">
                      <div className="font-display font-bold uppercase tracking-tight truncate text-sm sm:text-base">
                        {t.name}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-0.5">
                        {t.season || t.sport}
                      </div>
                      <div className="grid grid-cols-5 gap-1 mt-3 text-center">
                        {sportDef.standings_columns.map((c) => (
                          <div key={c.key}>
                            <div className="text-[9px] uppercase tracking-[0.15em] text-zinc-600">
                              {c.label}
                            </div>
                            <div
                              className="font-display font-black text-sm tabular-nums"
                              style={
                                c.key === "points" ? { color: accent } : {}
                              }
                            >
                              {t.standing[c.key] ?? 0}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* HIGHLIGHTS */}
        {settings?.show_tournament_videos !== false &&
          team.highlights?.length > 0 && (
            <section data-testid="team-highlights" style={secStyle("videos")}>
              <div className="flex items-center gap-3 mb-5">
                <Video className="w-4 h-4 text-zinc-400" />
                <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight">
                  Vidéos
                </h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {team.highlights.map((h) => (
                  <div key={h.id || h.url}>
                    <HighlightEmbed h={h} />
                    {h.title && (
                      <div className="mt-2 text-xs uppercase tracking-[0.18em] text-zinc-400">
                        {h.title}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

        {/* PALMARÈS */}
        {team.achievements?.length > 0 && (
          <section data-testid="team-achievements" style={secStyle("achievements")}>
            <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight mb-5">
              Palmarès
            </h2>
            <ul className="space-y-2">
              {team.achievements.map((a) => (
                <li
                  key={a.id || a.title}
                  className="metal-card rounded-xl p-4 flex items-start gap-4"
                >
                  <div
                    className="font-display text-xl font-black tabular-nums shrink-0"
                    style={{ color: accent }}
                  >
                    {a.date}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold uppercase tracking-tight">
                      {a.title}
                    </div>
                    {a.description && (
                      <div className="text-zinc-400 text-sm font-body mt-1">
                        {a.description}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* GALERIE */}
        {team.gallery?.length > 0 && (
          <section data-testid="team-gallery" style={secStyle("gallery")}>
            <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight mb-5">
              Galerie
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {team.gallery.map((g) => (
                <div
                  key={g.id || g.url}
                  className="aspect-square rounded-xl overflow-hidden border border-white/10"
                >
                  <img
                    src={g.url}
                    alt={g.caption || ""}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* QR CODE — visible uniquement pour l'administrateur (gravure) */}
        {user?.role === "admin" && (
          <section className="max-w-md" data-testid="team-qr" style={{ order: 99 }}>
            <QRCodeCard
              url={profileUrl}
              title="Plaque équipe connectée"
              subtitle="Réservé à l'administration — télécharge le PNG/SVG pour la gravure."
              fileBase={`team-${team.slug}`}
              primaryColor={accent}
            />
          </section>
        )}
      </div>
    </div>
    </ScanReveal>
  );
}
